import {
  Component,
  OnInit,
} from "@angular/core";
import {
  faPencil,
  faEraser,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../interfaces/form-field.model";
import { UserRightsManagementService } from "src/app/services/administration/user-rights-management.service";
import { NotifierService } from "angular-notifier";

@Component({
  selector: "app-user-rights-management",
  templateUrl: "./user-rights-management.component.html",
  styleUrls: ["./user-rights-management.component.css"],
})
export class UserRightsManagementComponent implements OnInit {
  // ICONS
  faPencil = faPencil;
  faEraser = faEraser;
  faCopy = faCopy;
  individual = false;

  activeUsers: any[] = [];
  screens: any[] = [];
  selectedUserId: string = "";
  selectedmenuId: number = 0;
  filterText: string = "";

  allUserRights: any[] = [];
  types_select: any[] = [];
  filteredTypesSelect: any[] = [];

  readonly companies = ["HYUNDAI", "CAPELLA", "C&D"];
  targetCompanies = [...this.companies];
  referenceUsers: any[] = [];
  targetUsers: any[] = [];
  originCompany = "";
  originUserId = "";
  targetCompany = "";
  targetUserId = "";
  copyOption = 1;
  replicationSuccessful = false;

  upperFormFields: FormField[] = [
    {
      type: "select",
      id: "user",
      label: "",
      options: [],
    },
    {
      type: "select",
      id: "right",
      label: "",
      options: [],
    },
    {
      type: "text",
      id: "menuName",
      label: "",
    },
  ];

  constructor(
    private UserRightsManagementService: UserRightsManagementService,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.loadActiveUsers();
    this.loadScreens();
    this.buildReplicationFields();
  }

  loadActiveUsers(): void {
    this.UserRightsManagementService.getAllActiveUsers().subscribe(
      (response) => {
        this.activeUsers = response.d.map((user: any) => ({
          id: user.UserId,
          display: user.FullName,
          value: user.UserId,
        }));
        this.updateUpperFormFields();
      },
      (error) => {
        console.error("Error getting active users:", error);
      },
    );
  }

  loadScreens(): void {
    this.UserRightsManagementService.getAllScreens().subscribe(
      (response) => {
        this.screens = response.d.map((screen: any) => ({
          id: screen.MenuId,
          display: screen.MenuName,
          value: screen.MenuId,
          menuName: screen.MenuName,
          parentMenuId: screen.ParentMenuId,
        }));

        // Adds "All" at the beginning of the list
        this.screens.unshift({
          id: 0,
          display: "All",
          value: 0,
          menuName: "All",
          parentMenuId: null,
        });

        this.updateUpperFormFields();
      },
      (error) => {
        console.error("Error getting screens:", error);
      },
    );
  }

  updateUpperFormFields(): void {
    this.upperFormFields = [
      {
        type: "select",
        id: "user",
        label: "",
        options: this.activeUsers,
        value: this.selectedUserId || undefined,
      },
      {
        type: "select",
        id: "right",
        label: "",
        options: this.screens,
        value: this.selectedmenuId,
      },
      {
        type: "text",
        id: "menuName",
        label: "",
        value: this.filterText,
      },
    ];
  }

  fetchAndPrepareUserRights(): void {
    if (this.selectedUserId && this.selectedmenuId === 0) {
      this.UserRightsManagementService.getUserRightsById(
        this.selectedUserId,
      ).subscribe(
        (response) => {
          this.storeUserRights(response);
          this.applyFilter();
        },
        (error) => {
          console.error("Error fetching rights:", error);
        },
      );
    } else if (
      this.selectedUserId &&
      this.selectedmenuId !== null &&
      this.selectedmenuId !== 0
    ) {
      this.UserRightsManagementService.getUserRightsByIdAndScreen(
        this.selectedUserId,
        this.selectedmenuId,
      ).subscribe(
        (response) => {
          this.storeUserRights(response);
          this.applyFilter();
        },
        (error) => {
          console.error("Error fetching rights with screen:", error);
        },
      );
    }
  }

  storeUserRights(data: any) {
    this.allUserRights = data.d.map((item: any) => ({
      RightsMasterId:
        item.RightsMasterId || "00000000-0000-0000-0000-000000000000",
      menuId: item.MenuId,
      menuName: item.MenuName,
      IsAddAllowed: item.IsAddAllowed,
      IsUpdateAllowed: item.IsUpdateAllowed,
      IsDeleteAllowed: item.IsDeleteAllowed,
      IsPrintAllowed: item.IsPrintAllowed,
      IsViewAllowed: item.IsViewAllowed,
      IsAppear: item.IsAppear,
      IsAddBelongToRoleRight: item.IsAddBelongToRoleRight,
      IsUpdateBelongToRoleRight: item.IsUpdateBelongToRoleRight,
      IsDeleteBelongToRoleRight: item.IsDeleteBelongToRoleRight,
      IsPrintBelongToRoleRight: item.IsPrintBelongToRoleRight,
      IsViewBelongToRoleRight: item.IsViewBelongToRoleRight,
      parentMenuId: item.ParentMenuId,
    }));

    this.types_select = this.allUserRights.map((item: any) => ({
      menuId: item.menuId,
      menuName: item.menuName,
      IsAddAllowed: {
        value: item.IsAddAllowed,
        clicked: item.IsAddBelongToRoleRight,
      },
      IsUpdateAllowed: {
        value: item.IsUpdateAllowed,
        clicked: item.IsUpdateBelongToRoleRight,
      },
      IsDeleteAllowed: {
        value: item.IsDeleteAllowed,
        clicked: item.IsDeleteBelongToRoleRight,
      },
      IsPrintAllowed: {
        value: item.IsPrintAllowed,
        clicked: item.IsPrintBelongToRoleRight,
      },
      IsViewAllowed: {
        value: item.IsViewAllowed,
        clicked: item.IsViewBelongToRoleRight,
      },
    }));

    this.filteredTypesSelect = [...this.types_select];
    this.applyFilter();
  }

  applyFilter(): void {
    let filteredData = this.types_select;

    if (this.selectedmenuId !== 0) {
      filteredData = filteredData.filter(
        (item) => item.menuId === this.selectedmenuId,
      );
    }

    if (this.filterText) {
      const lowerCaseFilter = this.filterText.toLowerCase();
      filteredData = filteredData.filter((item) =>
        item.menuName.toLowerCase().includes(lowerCaseFilter),
      );
    }

    this.filteredTypesSelect = filteredData;
  }

  handleUpdate(event: any): void {
    const changedField = event.fields[event.updatedValue];
    if (!changedField) {
      return;
    }

    if (changedField.id === "user") {
      this.selectedUserId = changedField.value || "";
      this.selectedmenuId = 0;
      this.filterText = "";
      this.types_select = [];
      this.filteredTypesSelect = [];
      this.updateUpperFormFields();
      this.fetchAndPrepareUserRights();
      return;
    }

    if (changedField.id === "right") {
      this.selectedmenuId = Number(changedField.value) || 0;
      this.fetchAndPrepareUserRights();
      return;
    }

    if (changedField.id === "menuName") {
      this.filterText = changedField.value || "";
      this.applyFilter();
    }
  }

  checkEntry(event: { entry: any; fieldId: string }): void {
    const storedEntry = this.types_select.find(
      (entry) => entry.menuId === event.entry.menuId,
    );
    if (storedEntry && storedEntry !== event.entry) {
      storedEntry[event.fieldId] = event.entry[event.fieldId];
    }
    this.applyFilter();
  }

  updateUserRights(): void {
    if (!this.selectedUserId || this.types_select.length === 0) {
      this.notifier.notify(
        "warning",
        "Please select a user with available rights",
      );
      return;
    }

    const preparedData = this.prepareUpdateData();
    this.UserRightsManagementService.updateUserRights(
      preparedData,
    ).subscribe(
      (response) => {
        const result = response?.d;
        if (Number(result?.Code) !== 0) {
          this.notifier.notify(
            "error",
            this.getApiMessage(response, "User rights were not updated"),
          );
          return;
        }

        this.notifier.notify(
          "success",
          this.getApiMessage(response, "User rights updated successfully"),
        );
        this.fetchAndPrepareUserRights();
      },
      (error) => {
        this.notifier.notify(
          "error",
          this.getApiMessage(error, "Error updating user rights"),
        );
        console.error("Error updating user rights:", error);
      },
    );
  }

  prepareUpdateData(): any {
    const updatedRights = this.types_select.map((type) => {
      const correspondingRight = this.allUserRights.find(
        (right) => right.menuId === type.menuId,
      );
      return {
        ...correspondingRight,
        IsAddAllowed: type.IsAddAllowed.value,
        IsUpdateAllowed: type.IsUpdateAllowed.value,
        IsDeleteAllowed: type.IsDeleteAllowed.value,
        IsPrintAllowed: type.IsPrintAllowed.value,
        IsViewAllowed: type.IsViewAllowed.value,
      };
    });

    const userRightsData = updatedRights.map((item) => ({
      RightsMasterId: item.RightsMasterId,
      MenuId: item.menuId,
      ParentMenuId: item.parentMenuId,
      MenuName: item.menuName,
      IsAddAllowed: item.IsAddAllowed,
      IsUpdateAllowed: item.IsUpdateAllowed,
      IsDeleteAllowed: item.IsDeleteAllowed,
      IsPrintAllowed: item.IsPrintAllowed,
      IsViewAllowed: item.IsViewAllowed,
      IsAppear: item.IsAppear,
      IsAddBelongToRoleRight: item.IsAddBelongToRoleRight,
      IsUpdateBelongToRoleRight: item.IsUpdateBelongToRoleRight,
      IsDeleteBelongToRoleRight: item.IsDeleteBelongToRoleRight,
      IsPrintBelongToRoleRight: item.IsPrintBelongToRoleRight,
      IsViewBelongToRoleRight: item.IsViewBelongToRoleRight,
    }));

    return {
      model: {
        UserId: this.selectedUserId,
        UserRights: userRightsData,
      },
      identityUserId: this.selectedUserId,
    };
  }

  referenceUserFields: FormField[] = [];
  targetUserFields: FormField[] = [];

  onIndividualChange(individual: boolean): void {
    if (individual && this.selectedUserId && this.types_select.length === 0) {
      this.fetchAndPrepareUserRights();
    }
  }

  handleReferenceUpdate(event: any): void {
    const changedField = event.fields[event.updatedValue];
    if (!changedField) {
      return;
    }

    if (changedField.id === "originCompany") {
      this.originCompany = changedField.value || "";
      this.originUserId = "";
      this.referenceUsers = [];
      this.targetCompany = "";
      this.targetUserId = "";
      this.targetUsers = [];
      this.targetCompanies = this.getCompatibleTargetCompanies(
        this.originCompany,
      );
      this.replicationSuccessful = false;
      this.buildReplicationFields();
      if (this.originCompany) {
        this.loadReplicationUsers(this.originCompany, false);
      }
      return;
    }

    if (changedField.id === "originUser") {
      this.originUserId = changedField.value || "";
      this.targetCompany = "";
      this.targetUserId = "";
      this.targetUsers = [];
      this.replicationSuccessful = false;
      this.buildReplicationFields();
    }
  }

  handleTargetUpdate(event: any): void {
    const changedField = event.fields[event.updatedValue];
    if (!changedField) {
      return;
    }

    if (changedField.id === "targetCompany") {
      this.targetCompany = changedField.value || "";
      this.targetUserId = "";
      this.targetUsers = [];
      this.replicationSuccessful = false;
      this.buildReplicationFields();
      if (this.targetCompany) {
        this.loadReplicationUsers(this.targetCompany, true);
      }
      return;
    }

    if (changedField.id === "targetUser") {
      this.targetUserId = changedField.value || "";
      this.replicationSuccessful = false;
      this.buildReplicationFields();
    } else if (changedField.id === "replicationOptions") {
      this.copyOption = Number(changedField.value) || 1;
      this.replicationSuccessful = false;
    }
  }

  replicateRights(): void {
    if (!this.isReplicationValid()) {
      this.notifier.notify("warning", "Please complete all required fields");
      return;
    }

    const payload = {
      userIdOrigin: this.originUserId,
      userIdTarget: this.targetUserId,
      databaseOrigin: this.originCompany,
      databaseTarget: this.targetCompany,
      copyOption: this.copyOption,
    };

    this.UserRightsManagementService.copyUserPermissions(payload).subscribe({
      next: (response) => {
        if (Number(response?.d?.Code) !== 0) {
          this.notifier.notify(
            "error",
            this.getApiMessage(response, "Permissions were not replicated"),
          );
          return;
        }

        this.replicationSuccessful = true;
        this.notifier.notify(
          "success",
          this.getApiMessage(response, "Permissions replicated successfully"),
        );
      },
      error: (error) => {
        this.notifier.notify(
          "error",
          this.getApiMessage(error, "Error replicating permissions"),
        );
        console.error("Error replicating permissions:", error);
      },
    });
  }

  clearReplication(): void {
    this.originCompany = "";
    this.originUserId = "";
    this.targetCompany = "";
    this.targetUserId = "";
    this.copyOption = 1;
    this.referenceUsers = [];
    this.targetUsers = [];
    this.targetCompanies = [...this.companies];
    this.replicationSuccessful = false;
    this.buildReplicationFields();
  }

  isReplicationValid(): boolean {
    return Boolean(
      this.originCompany &&
        this.originUserId &&
        this.targetCompany &&
        this.targetUserId &&
        [1, 2].includes(this.copyOption),
    );
  }

  private loadReplicationUsers(companyName: string, isTarget: boolean): void {
    this.UserRightsManagementService.getUsersDropdownList(
      companyName,
    ).subscribe({
      next: (response) => {
        const selectedCompany = isTarget
          ? this.targetCompany
          : this.originCompany;
        if (selectedCompany !== companyName) {
          return;
        }

        const users = Array.isArray(response?.d)
          ? response.d.map((user: any) => ({
              display: `${user.LoginId} (${user.DisplayName})`,
              value: user.UserReferenceId,
            }))
          : [];

        if (isTarget) {
          this.targetUsers = users;
        } else {
          this.referenceUsers = users;
        }
        this.buildReplicationFields();
      },
      error: (error) => {
        this.notifier.notify(
          "error",
          this.getApiMessage(error, "Error getting users"),
        );
        console.error("Error getting users for rights replication:", error);
      },
    });
  }

  private getCompatibleTargetCompanies(originCompany: string): string[] {
    if (originCompany === "HYUNDAI" || originCompany === "CAPELLA") {
      return ["HYUNDAI", "CAPELLA"];
    }
    if (originCompany === "C&D") {
      return ["C&D"];
    }
    return [...this.companies];
  }

  private buildReplicationFields(): void {
    const companyOptions = this.companies.map((company) => ({
      display: company,
      value: company,
    }));
    const targetCompanyOptions = this.targetCompanies.map((company) => ({
      display: company,
      value: company,
    }));

    this.referenceUserFields = [
      {
        type: "select",
        id: "originCompany",
        label: "Company",
        required: true,
        options: companyOptions,
        value: this.originCompany || undefined,
      },
      {
        type: "select",
        id: "originUser",
        label: "User",
        required: true,
        options: this.referenceUsers,
        value: this.originUserId || undefined,
        lock: !this.originCompany,
      },
    ];

    this.targetUserFields = [
      {
        type: "select",
        id: "targetCompany",
        label: "Company",
        required: true,
        options: targetCompanyOptions,
        value: this.targetCompany || undefined,
        lock: !this.originUserId,
      },
      {
        type: "select",
        id: "targetUser",
        label: "User",
        required: true,
        options: this.targetUsers,
        value: this.targetUserId || undefined,
        lock: !this.targetCompany,
      },
      {
        type: "radio",
        id: "replicationOptions",
        label: "Replication Options",
        required: true,
        value: this.copyOption,
        lock: !this.targetUserId,
        options: [
          { display: "Add to Existing Rights", value: 1 },
          { display: "Replace Existing Rights", value: 2 },
        ],
      },
    ];
  }

  private getApiMessage(response: any, fallback: string): string {
    const payload = response?.error?.d ?? response?.error ?? response?.d;
    if (typeof payload === "string" && payload.trim()) {
      return payload;
    }
    return (
      payload?.Message ||
      payload?.message ||
      response?.Message ||
      response?.message ||
      fallback
    );
  }

  tableHeaders = [
    {
      title: "SCREEN ID",
      type: "data",
      classes: "max-w-64",
      id: "menuId",
    },
    {
      title: "SCREEN NAME",
      type: "data",
      id: "menuName",
    },
    {
      title: "RIGHTS ADD",
      type: "checkbox",
      id: "IsAddAllowed",
    },
    {
      title: "RIGHTS UPDATE",
      type: "checkbox",
      id: "IsUpdateAllowed",
    },
    {
      title: "RIGHTS DELETE",
      type: "checkbox",
      id: "IsDeleteAllowed",
    },
    {
      title: "RIGHTS PRINT",
      type: "checkbox",
      id: "IsPrintAllowed",
    },
    {
      title: "RIGHTS VIEW",
      type: "checkbox",
      id: "IsViewAllowed",
    },
  ];
}
